<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  /** i18n namespace, allowing reuse for other synthesis flows (e.g. CSAT). */
  i18nPrefix: {
    type: String,
    default: 'CONVERSATION_TRANSCRIPTS_SYNTHESIS',
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t, tm } = useI18n();

const tk = key => t(`${props.i18nPrefix}.${key}`);

const defaultQuestionsList = computed(() => {
  const raw = tm(`${props.i18nPrefix}.DEFAULT_QUESTIONS`);
  return Array.isArray(raw)
    ? raw.filter(q => typeof q === 'string' && q.trim())
    : [];
});

const dialogRef = ref(null);
const initialFocusRef = ref(null);
const questionRows = ref(['']);
/** When true, send one free-form prompt; when false, send multiple question lines. */
const usePromptMode = ref(false);
const customPrompt = ref('');

const reset = () => {
  questionRows.value = [''];
  usePromptMode.value = false;
  customPrompt.value = '';
};

const open = () => {
  reset();
  dialogRef.value?.open();
  requestAnimationFrame(() => {
    initialFocusRef.value?.focus();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const onDialogClose = () => {
  reset();
  emit('close');
};

const addQuestionRow = () => {
  questionRows.value = [...questionRows.value, ''];
};

const removeQuestionRow = index => {
  if (questionRows.value.length <= 1) {
    questionRows.value = [''];
    return;
  }
  questionRows.value = questionRows.value.filter((_, i) => i !== index);
};

const onConfirm = () => {
  let questions;
  if (usePromptMode.value) {
    const p = customPrompt.value.trim();
    questions = p ? [p] : [];
  } else {
    questions = questionRows.value.map(q => q.trim()).filter(Boolean);
  }
  emit('confirm', questions);
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    :cancel-button-label="tk('CANCEL')"
    :title="tk('TITLE')"
    :description="tk('DESCRIPTION')"
    @close="onDialogClose"
  >
    <div class="flex flex-col gap-4">
      <div ref="initialFocusRef" tabindex="-1" />
      <div
        class="flex flex-col gap-2 rounded-lg border border-n-slate-4 bg-n-alpha-2 p-3"
      >
        <p class="mb-0 text-sm text-n-slate-11">
          {{ tk('DEFAULTS_INTRO') }}
        </p>
        <ol class="mb-0 list-decimal space-y-1.5 pl-5 text-sm text-n-slate-12">
          <li v-for="(q, i) in defaultQuestionsList" :key="i">
            {{ q }}
          </li>
        </ol>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ tk('EMPTY_USES_DEFAULTS') }}
        </p>
      </div>

      <label
        class="flex cursor-pointer items-start gap-3 rounded-lg border border-n-slate-4 bg-n-alpha-1 p-3"
      >
        <Checkbox
          v-model="usePromptMode"
          class="mt-0.5"
          :disabled="isLoading"
        />
        <span class="text-sm text-n-slate-12">
          {{ tk('USE_PROMPT_MODE') }}
        </span>
      </label>

      <div v-if="!usePromptMode" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ tk('QUESTIONS_LABEL') }}
        </span>
        <div
          v-for="(row, index) in questionRows"
          :key="index"
          class="flex items-start gap-2"
        >
          <input
            v-model="questionRows[index]"
            type="text"
            class="reset-base h-10 min-w-0 flex-1 rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 py-2 text-sm !text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:border-n-brand"
            :placeholder="tk('QUESTION_PLACEHOLDER')"
            :disabled="isLoading"
          />
          <button
            v-if="questionRows.length > 1"
            type="button"
            class="mt-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-n-slate-3 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-50"
            :title="tk('REMOVE_QUESTION')"
            :disabled="isLoading"
            @click="removeQuestionRow(index)"
          >
            <span class="i-lucide-minus size-4" aria-hidden="true" />
            <span class="sr-only">{{ tk('REMOVE_QUESTION') }}</span>
          </button>
        </div>

        <button
          type="button"
          class="inline-flex items-center gap-2 self-start rounded-lg border border-n-slate-3 px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="isLoading"
          @click="addQuestionRow"
        >
          <span class="i-lucide-plus size-4" aria-hidden="true" />
          {{ tk('ADD_QUESTION') }}
        </button>
      </div>

      <div v-else class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ tk('PROMPT_LABEL') }}
        </span>
        <textarea
          v-model="customPrompt"
          rows="7"
          class="min-h-[8rem] w-full resize-y rounded-lg border border-n-slate-5 !bg-n-solid-1 px-3 py-2 text-sm !text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:border-n-brand"
          :placeholder="tk('PROMPT_PLACEHOLDER')"
          :disabled="isLoading"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ tk('PROMPT_HELP') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div
        class="relative z-10 flex w-full flex-col gap-2 sm:flex-row sm:justify-end"
      >
        <Button
          variant="faded"
          color="slate"
          class="w-full sm:w-auto"
          type="button"
          :label="tk('CANCEL')"
          :disabled="isLoading"
          @click="close"
        />
        <Button
          color="blue"
          class="w-full sm:w-auto"
          type="button"
          :label="tk('CONFIRM')"
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="onConfirm"
        />
      </div>
    </template>
  </Dialog>
</template>
